import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { toast } from "react-toastify";
import loadergif from "../../../img/landing/loader123.gif";
import { procurment_url, fmb_schedule_url } from "../../../Redux/Reducer/AuthReducer";

// The reporting year runs Oct 1 - Sep 30. The first one shown is the year
// starting Oct 2023 (the report data itself begins in Nov 2023).
const YEAR_START_MONTH = 10;
const FIRST_START_YEAR = 2023;

// Bar colours: the latest completed year (the headline number) is the
// darkest, older years fade back, the year still in progress is grey.
const COLOR_HEADLINE = "#591333";
const COLOR_PREVIOUS = "#AE8340";
const COLOR_OLDER = "#D8C7A3";
const COLOR_IN_PROGRESS = "#B9B6CC";

// admin/user-report only returns per-user totals for whatever date range it
// is given - there's no per-day breakdown - so "Maximum Daily Thaali" has to
// be built from one single-day request per thaali day. Those run a few at a
// time, and finished days are kept in localStorage (a past day's count
// doesn't change) so only days not seen before are fetched on later visits.
const DAILY_CACHE_KEY = "report_annual_daily_thaali_v1";
const DAILY_FETCH_CONCURRENCY = 6;
// Recent days can still change (late registrations/skips), so they're
// always re-fetched rather than trusted from the cache.
const CACHE_MIN_AGE_DAYS = 7;

function toIsoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatNumber(value) {
  return Math.round(value).toLocaleString("en-US");
}

function formatDayLabel(isoDate) {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// "Apr 27 and Aug 28, 2024" when every day is in the same year, otherwise
// each date written out in full.
function formatDayList(isoDates) {
  if (isoDates.length === 0) return "";
  const years = new Set(isoDates.map((d) => d.slice(0, 4)));
  if (years.size > 1) return joinWithAnd(isoDates.map(formatDayLabel));
  const short = isoDates.map((iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  });
  return `${joinWithAnd(short)}, ${isoDates[0].slice(0, 4)}`;
}

function joinWithAnd(parts) {
  if (parts.length <= 2) return parts.join(" and ");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

function fetchUserReport(startDate, endDate) {
  return fetch(procurment_url + "admin/user-report", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: localStorage.getItem("access_token"),
    },
    body: JSON.stringify({ start_date: startDate, end_date: endDate }),
  })
    .then((res) => res.json())
    .then((data) => {
      if (data?.status !== true) throw new Error(data?.message || "Failed to load report");
      const rows = Array.isArray(data.data) ? data.data : [];
      // user_count = thaalis that user received in the range;
      // total_schedule_count = thaali days in the range (same on every row).
      const total = rows.reduce((sum, row) => sum + (Number(row.user_count) || 0), 0);
      const days = rows.reduce((max, row) => Math.max(max, Number(row.total_schedule_count) || 0), 0);
      return { total, days };
    });
}

// FMB thaalis whose remarks (notes) say "Sihori Packet" are a packet
// handed out alongside the day's regular thaali, not a regular thaali -
// Maximum Daily Thaali is shown without them (and with, in the hover).
function isSihoriPacket(item) {
  return /sihori\s*packet/i.test(item?.notes || "");
}

function scheduleDayToIso(scheduleDay) {
  const [month, day, year] = String(scheduleDay || "").split("-"); // "MM-DD-YYYY"
  if (!Number(year) || !Number(month) || !Number(day)) return null;
  return `${year}-${month}-${day}`;
}

// Every FMB thaali day (YYYY-MM-DD) from the schedule, so the per-day
// requests only cover days that actually had a thaali - plus which of
// those days include a Sihori Packet thaali.
function fetchThaaliDays() {
  return fetch(fmb_schedule_url + "admin/thali-list", {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: localStorage.getItem("access_token"),
    },
  })
    .then((res) => res.json())
    .then((data) => {
      const days = new Set();
      const sihoriDays = new Set();
      (data?.data || []).forEach((item) => {
        if (item.schedule_type !== "FMBTHALI") return;
        const iso = scheduleDayToIso(item.schedule_day);
        if (!iso) return;
        days.add(iso);
        if (isSihoriPacket(item)) sihoriDays.add(iso);
      });
      return { days: Array.from(days).sort(), sihoriDays: Array.from(sihoriDays).sort() };
    });
}

// admin/user-report can't separate two thaalis on the same day, so for a
// Sihori Packet day the count WITHOUT the packet comes from the Thaali
// Schedule's own per-thaali count (admin/thaali-list-date) for that day's
// other FMB thaali(s).
function fetchDayCountWithoutSihori(day) {
  return fetch(fmb_schedule_url + "admin/thaali-list-date", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: localStorage.getItem("access_token"),
    },
    body: JSON.stringify({ start_date: day, end_date: day }),
  })
    .then((res) => res.json())
    .then((data) => {
      if (data?.status !== true) throw new Error(data?.message || "Failed to load thaali counts");
      return (data.data || [])
        .filter((item) => item.schedule_type === "FMBTHALI" && !isSihoriPacket(item))
        .reduce((sum, item) => sum + (Number(item.thaaliCount) || 0), 0);
    });
}

function readDailyCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(DAILY_CACHE_KEY));
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeDailyCache(cache) {
  try {
    localStorage.setItem(DAILY_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // storage full/unavailable - the numbers still show, just not remembered
  }
}

function describeChange(current, previous, suffix) {
  if (previous == null || current == null) return "";
  const diff = Math.round(current) - Math.round(previous);
  if (diff === 0) return `Same as ${suffix}`;
  return `${diff > 0 ? "Up" : "Down"} ${formatNumber(Math.abs(diff))} from ${suffix}`;
}

function MetricCard({ title, value, change, changeDirection, note, labels, data, colors, pending, tooltipCustom }) {
  return (
    <div className="annual-summary-card">
      <div className="annual-summary-card-title">{title}</div>
      <div className="annual-summary-card-value">{value}</div>
      <div className={`annual-summary-card-change ${changeDirection ? `is-${changeDirection}` : ""}`}>
        {change || " "}
      </div>
      <ReactApexChart
        type="bar"
        height={240}
        series={[{ name: title, data }]}
        options={{
          chart: { toolbar: { show: false } },
          plotOptions: {
            bar: { columnWidth: "55%", borderRadius: 4, distributed: true, dataLabels: { position: "top" } },
          },
          dataLabels: {
            enabled: true,
            offsetY: -20,
            formatter: (val) => (val > 0 ? formatNumber(val) : "-"),
            style: { fontSize: "12px", colors: ["#1A1A1A"] },
          },
          legend: { show: false },
          colors,
          xaxis: { categories: labels, labels: { style: { fontSize: "12px" } } },
          yaxis: { labels: { formatter: (val) => formatNumber(val), style: { fontSize: "11px" } } },
          grid: { borderColor: "#F0F0F0", padding: { top: 10 } },
          tooltip: tooltipCustom
            ? { custom: ({ dataPointIndex }) => tooltipCustom(dataPointIndex) }
            : { y: { formatter: (val) => formatNumber(val) } },
        }}
      />
      <div className="annual-summary-card-note">{pending || note || " "}</div>
    </div>
  );
}

export default function AnnualSummary() {
  // One period per Oct-Sep year from FIRST_START_YEAR on; the last one is
  // the year in progress (Oct 1 to today).
  const periods = useMemo(() => {
    const today = new Date();
    const todayIso = toIsoDate(today);
    const currentStartYear = today.getMonth() + 1 >= YEAR_START_MONTH ? today.getFullYear() : today.getFullYear() - 1;
    const startMonth = String(YEAR_START_MONTH).padStart(2, "0");
    const list = [];
    for (let y = Math.min(FIRST_START_YEAR, currentStartYear); y <= currentStartYear; y += 1) {
      const isCurrent = y === currentStartYear;
      const lastDay = new Date(y + 1, YEAR_START_MONTH - 1, 0); // day before next year's start
      const shortLabel = `${y}-${String(y + 1).slice(2)}`;
      list.push({
        isCurrent,
        shortLabel,
        label: isCurrent ? `${shortLabel} (to date)` : shortLabel,
        start: `${y}-${startMonth}-01`,
        end: isCurrent ? todayIso : toIsoDate(lastDay),
      });
    }
    return list;
  }, []);

  const [loading, setLoading] = useState(true);
  const [yearStats, setYearStats] = useState(null); // [{ total, days }] per period
  const [dailyCounts, setDailyCounts] = useState({}); // "YYYY-MM-DD" -> thaalis that day
  const [dailyProgress, setDailyProgress] = useState({ done: 0, total: 0, finished: false });
  // Sihori Packet days only: "YYYY-MM-DD" -> that day's count without the packet.
  const [withoutSihoriCounts, setWithoutSihoriCounts] = useState({});

  // Totals/averages: one request per year.
  useEffect(() => {
    let cancelled = false;
    Promise.all(periods.map((p) => fetchUserReport(p.start, p.end)))
      .then((results) => {
        if (cancelled) return;
        setYearStats(results);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) toast.error("Failed to load annual summary");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [periods]);

  // Maximum daily thaali: per-day counts, cached days first, the rest fetched.
  useEffect(() => {
    let cancelled = false;
    const rangeStart = periods[0].start;
    const rangeEnd = periods[periods.length - 1].end;
    const cacheCutoff = new Date();
    cacheCutoff.setDate(cacheCutoff.getDate() - CACHE_MIN_AGE_DAYS);
    const cacheCutoffIso = toIsoDate(cacheCutoff);

    fetchThaaliDays()
      .then(({ days: allDays, sihoriDays }) => {
        if (cancelled) return undefined;
        const days = allDays.filter((d) => d >= rangeStart && d <= rangeEnd);
        // Only a handful of days at most, so these aren't cached.
        const sihoriInRange = sihoriDays.filter((d) => d >= rangeStart && d <= rangeEnd);
        Promise.all(
          sihoriInRange.map((d) =>
            fetchDayCountWithoutSihori(d)
              .then((count) => [d, count])
              .catch((error) => {
                console.error(error);
                return null;
              })
          )
        ).then((entries) => {
          if (!cancelled) setWithoutSihoriCounts(Object.fromEntries(entries.filter(Boolean)));
        });
        const cache = readDailyCache();
        const counts = {};
        const pending = [];
        days.forEach((d) => {
          if (d < cacheCutoffIso && typeof cache[d] === "number") counts[d] = cache[d];
          else pending.push(d);
        });
        setDailyCounts({ ...counts });
        setDailyProgress({ done: days.length - pending.length, total: days.length, finished: pending.length === 0 });
        if (pending.length === 0) return undefined;

        // Newest first, so the current year's maximum settles soonest.
        const queue = pending.reverse();
        let done = days.length - pending.length;
        let sinceFlush = 0;
        const flush = (finished) => {
          if (cancelled) return;
          setDailyCounts({ ...counts });
          setDailyProgress({ done, total: days.length, finished });
          writeDailyCache({ ...cache, ...Object.fromEntries(Object.entries(counts).filter(([d]) => d < cacheCutoffIso)) });
        };
        const worker = async () => {
          while (!cancelled && queue.length > 0) {
            const day = queue.shift();
            try {
              const { total } = await fetchUserReport(day, day);
              counts[day] = total;
            } catch (error) {
              console.error(error); // that day is left out of the maximum
            }
            done += 1;
            sinceFlush += 1;
            if (sinceFlush >= 20) {
              sinceFlush = 0;
              flush(false);
            }
          }
        };
        return Promise.all(Array.from({ length: DAILY_FETCH_CONCURRENCY }, worker)).then(() => flush(true));
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setDailyProgress((prev) => ({ ...prev, finished: true }));
      });
    return () => {
      cancelled = true;
    };
  }, [periods]);

  // Highest single-day count per year, with every day that reached it
  // (ties are possible, e.g. two days at 191) - once counting everything
  // (withSihori) and once with Sihori Packet thaalis left out (the number
  // the card and bars show).
  const maxByPeriod = useMemo(() => {
    const findBest = (p, countFor) => {
      let best = null;
      Object.keys(dailyCounts).forEach((day) => {
        if (day < p.start || day > p.end) return;
        const count = countFor(day);
        if (!best || count > best.count) best = { count, days: [day] };
        else if (count === best.count) best.days.push(day);
      });
      if (best) best.days.sort();
      return best;
    };
    return periods.map((p) => ({
      withSihori: findBest(p, (day) => dailyCounts[day]),
      withoutSihori: findBest(p, (day) =>
        typeof withoutSihoriCounts[day] === "number" ? withoutSihoriCounts[day] : dailyCounts[day]
      ),
      hasSihori: Object.keys(withoutSihoriCounts).some((day) => day >= p.start && day <= p.end),
    }));
  }, [periods, dailyCounts, withoutSihoriCounts]);

  if (loading) {
    return (
      <div className="Loading-img">
        <img src={loadergif} alt="Loading" />
      </div>
    );
  }
  if (!yearStats) return <p className="annual-summary-empty">Annual summary could not be loaded.</p>;

  const labels = periods.map((p) => p.label);
  const averages = yearStats.map((s) => (s.days > 0 ? s.total / s.days : 0));
  const totals = yearStats.map((s) => s.total);
  const maxes = maxByPeriod.map((m) => (m.withoutSihori ? m.withoutSihori.count : 0));

  // Headline = the latest completed year, compared with the year before
  // it. (Only the in-progress year exists? Then that one is the headline.)
  const headIdx = periods.length > 1 ? periods.length - 2 : 0;
  const prevIdx = headIdx - 1;
  const head = periods[headIdx];
  const prev = prevIdx >= 0 ? periods[prevIdx] : null;
  const colors = periods.map((p, idx) => {
    if (p.isCurrent && periods.length > 1) return COLOR_IN_PROGRESS;
    if (idx === headIdx) return COLOR_HEADLINE;
    return idx === prevIdx ? COLOR_PREVIOUS : COLOR_OLDER;
  });
  const direction = (values) => {
    if (!prev) return "";
    const diff = Math.round(values[headIdx]) - Math.round(values[prevIdx]);
    return diff === 0 ? "" : diff > 0 ? "up" : "down";
  };
  const change = (values) => (prev ? describeChange(values[headIdx], values[prevIdx], prev.shortLabel) : "");

  const maxPending = dailyProgress.finished
    ? ""
    : `Checking each day's count... ${dailyProgress.done} of ${dailyProgress.total || "?"} days`;
  const headMax = maxByPeriod[headIdx].withoutSihori;
  const maxReady = dailyProgress.finished && maxes[headIdx] > 0 && prev && maxes[prevIdx] > 0;

  return (
    <div className="annual-summary">
      <div className="annual-summary-period">
        Year runs Oct 1 - Sep 30. Headline numbers are for <b>{head.shortLabel}</b> ({formatDayLabel(head.start)} -{" "}
        {formatDayLabel(head.end)}).
      </div>
      <div className="annual-summary-grid">
        <MetricCard
          title="Average Daily Thaali"
          value={formatNumber(averages[headIdx])}
          change={change(averages)}
          changeDirection={direction(averages)}
          note={`${yearStats[headIdx].days} thaali days in ${head.shortLabel}`}
          labels={labels}
          data={averages.map((v) => Math.round(v))}
          colors={colors}
        />
        <MetricCard
          title="Number of Thaalis Delivered"
          value={formatNumber(totals[headIdx])}
          change={change(totals)}
          changeDirection={direction(totals)}
          labels={labels}
          data={totals}
          colors={colors}
        />
        <MetricCard
          title="Maximum Daily Thaali"
          value={maxes[headIdx] > 0 ? formatNumber(maxes[headIdx]) : "-"}
          change={maxReady ? change(maxes) : ""}
          changeDirection={maxReady ? direction(maxes) : ""}
          note={
            headMax
              ? `Highest day: ${formatDayList(headMax.days)}${maxByPeriod[headIdx].hasSihori ? " - Sihori Packet not counted" : ""}`
              : ""
          }
          tooltipCustom={(idx) => {
            const { withSihori, withoutSihori, hasSihori } = maxByPeriod[idx];
            const line = (best) => (best && best.count > 0 ? `${formatNumber(best.count)} (${formatDayList(best.days)})` : "-");
            const rows = hasSihori
              ? [
                  ["Without Sihori Packet", line(withoutSihori)],
                  ["With Sihori Packet", line(withSihori)],
                ]
              : [["Maximum daily", line(withoutSihori)]];
            return `<div class="annual-summary-tip"><div class="annual-summary-tip-title">${periods[idx].shortLabel}</div>${rows
              .map(([label, text]) => `<div><span>${label}:</span> <b>${text}</b></div>`)
              .join("")}</div>`;
          }}
          pending={maxPending}
          labels={labels}
          data={maxes}
          colors={colors}
        />
      </div>
    </div>
  );
}
